<template>
  <content-dialog
    :visible.sync="modalVisible"
    fullscreen
    force-close-icon
    card-class="blue-grey lighten-5"
    title-padding="pa-3"
  >
    <template
      v-if="yachtModel"
      slot="dialogTitle"
    >
      <v-row
        v-if="yachtModel"
        no-gutters
        align="center"
      >
        <v-col
          cols="auto"
        >
          <v-avatar
            color="grey lighten-3"
            size="55px"
          >
            <v-img
              v-if="yachtModel.main_image"
              :src="yachtModel.main_image.path"
              alt="Main image"
            />
          </v-avatar>
        </v-col>
        <v-col class="fill-height px-6">
          <div>
            Configure
            {{ yachtModel.full_name }}
            &middot;
            <span class="grey--text">
              ID {{ yachtModel.id }}
            </span>
          </div>
          <div class="body-2">
            <span class="grey--text mr-1">For Charter Country:</span>
            <flag
              :iso="currentCountry.code"
              :squared="false"
              class="mx-1"
            />
            {{ currentCountry.name }}
          </div>
        </v-col>
        <v-col cols="auto">
          <v-btn
            color="primary"
            @click="saveConfiguration()"
          >
            Save configuration
          </v-btn>
        </v-col>
      </v-row>
    </template>

    <v-card-text
      v-if="yachtModel"
      class="pa-0"
    >
      <v-row
        class="fill-height"
        no-gutters
      >
        <v-col
          lg="12"
          class="pl-12 pr-4 pb-12 fill-height"
          style="overflow-y: auto"
        >
          <v-row
            no-gutters
            class="mt-4 pa-4"
          >
            <v-col>
              <div class="subtitle-1">
                Equipment
              </div>
            </v-col>
            <v-col
              cols="auto"
              class="pr-4"
            >
              <v-btn
                small
                outlined
                color="primary"
                @click="yachtEquipmentPackEdit.visible = true"
              >
                <v-icon
                  left
                  small
                >
                  add
                </v-icon>
                Add Custom Pack
              </v-btn>
            </v-col>
            <v-col cols="auto">
              <v-btn-toggle
                v-model="currentEquipmentType"
                color="primary"
                background-color="transparent"
                borderless
                mandatory
              >
                <v-btn
                  small
                  value="all"
                >
                  All
                </v-btn>
                <v-btn
                  small
                  value="required"
                >
                  Mandatory
                </v-btn>
                <v-btn
                  small
                  value="optional"
                >
                  Optional
                </v-btn>
                <v-btn
                  small
                  value="custom"
                >
                  Custom
                </v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>

          <div class="mb-4">
            <template v-for="category in categories">
              <v-chip
                :key="category.id"
                :style="{ opacity: currentCategoryId && !category.isFiltered ? 0.2 : 1 }"
                class="elevation-5 clickable mr-1 mb-1 font-weight-regular"
                small
                outlined
                color="grey darken-3"
                @click="currentCategoryId = (currentCategoryId === category.id) ? null : category.id"
              >
                {{ category.name }}
              </v-chip>
            </template>
          </div>

          <v-row dense>
            <v-col
              v-for="pack in filteredEquipmentPacks"
              :key="pack.id + (pack.custom ? 'custom' : '')"
              cols="16"
            >
              <v-card
                :elevation="pack.selected ? 10 : 0"
                class="fill-height"
                :style="{ borderTop: '2px solid ' + pack.category.color, opacity: pack.selected ? '1' : '0.5' }"
                :ripple="false"
              >
                <v-card-title
                  class="body-2 px-4 py-2 no-gutters clickable"
                  @click="pack.is_expanded = !pack.is_expanded"
                >
                  <v-col>
                    <span class="grey--text">{{ pack.category.name }}</span>
                    &middot;
                    {{ pack.label }}
                  </v-col>
                  <v-col
                    cols="auto"
                    class="grey--text caption pl-3"
                  >
                    {{ pack.code }}
                  </v-col>
                  <v-col
                    v-if="pack.is_original_different && updatesPullable"
                    cols="auto"
                    class="pl-3"
                  >
                    <v-chip
                      outlined
                      color="red"
                      @click="pullUpdatedPack(pack)"
                    >
                      Updates available - click to pull
                    </v-chip>
                  </v-col>
                  <v-col
                    v-if="!pack.custom && pack.is_original_removed && updatesPullable"
                    cols="auto"
                    class="pl-3"
                  >
                    <v-chip
                      outlined
                      color="red"
                      @click="removeUpdatedPack(pack)"
                    >
                      Obsolete - click to remove
                    </v-chip>
                  </v-col>
                  <v-col
                    v-if="pack.is_original_new"
                    cols="auto"
                    class="pl-3"
                  >
                    <v-chip
                      outlined
                      color="red"
                    >
                      Newly added
                    </v-chip>
                  </v-col>
                  <v-col
                    v-if="!pack.default || $can('view-sale-price', 'YachtEquipmentPack')"
                    cols="auto"
                    class="pl-3"
                  >
                    {{ pack.sale_price | currency(pack.currency, 0) }}
                  </v-col>
                  <!--                  <v-col-->
                  <!--                    cols="auto"-->
                  <!--                    class="pl-3 caption"-->
                  <!--                    :class="{ 'grey&#45;&#45;text': !pack.default, 'red&#45;&#45;text': pack.default }"-->
                  <!--                  >-->
                  <!--                    {{ pack.default ? 'Mandatory' : 'Optional' }}-->
                  <!--                  </v-col>-->
                  <v-col
                    v-if="pack.custom"
                    cols="auto"
                    class="pl-3"
                  >
                    <v-btn
                      small
                      text
                      rounded
                      @click.stop="yachtEquipmentPackEdit.equipmentPack = pack; yachtEquipmentPackEdit.visible = true"
                    >
                      Edit
                    </v-btn>
                  </v-col>
                  <v-col
                    cols="auto"
                    class="pl-3"
                  >
                    <v-btn
                      :color="pack.selected ? 'success' : 'primary'"
                      :dark="!(!!pack.default && pack.selected)"
                      small
                      rounded
                      :disabled="!$can('edit-default-equipment-packs', 'Yacht') && !!pack.default && pack.selected"
                      @click.stop="toggleEquipmentPack(pack.id)"
                    >
                      {{ pack.selected ? 'Added' : 'Add' }}
                    </v-btn>
                  </v-col>
                  <v-col
                    cols="auto"
                    class="pl-4"
                  >
                    <v-icon @click.stop="pack.is_expanded = !pack.is_expanded">
                      {{ pack.is_expanded ? 'expand_less' : 'expand_more' }}
                    </v-icon>
                  </v-col>
                </v-card-title>
                <v-expand-transition>
                  <div v-if="pack.is_expanded">
                    <v-divider />
                    <v-card-text v-if="!pack.is_configurable">
                      <div
                        v-for="(item, index) in pack.items"
                        :key="pack.id + '_' + index"
                        class="caption list--striped pa-1"
                      >
                        {{ item.label }}
                      </div>
                    </v-card-text>
                    <v-card-text v-else>
                      <v-row
                        v-for="(item, index) in pack.items"
                        :key="pack.id + '_' + index"
                        no-gutters
                        class="caption list--striped pa-1"
                      >
                        <v-col cols="auto">
                          <v-checkbox
                            v-model="item.is_selected"
                            class="pa-0 ma-0"
                            dense
                            :ripple="false"
                            hide-details
                            @change="calculateConfigurablePackPrice(pack)"
                          />
                        </v-col>
                        <v-col
                          class="clickable"
                          @click="item.is_selected = !item.is_selected; calculateConfigurablePackPrice(pack)"
                        >
                          {{ item.label }}
                        </v-col>
                        <v-col
                          v-if="$can('view-sale-price', 'YachtEquipmentPack')"
                          cols="auto"
                          class="pl-4"
                        >
                          <div class="text-no-wrap">
                            {{ item.sale_price | currency(pack.currency) }}
                          </div>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </div>
                </v-expand-transition>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
        <!--<editor-fold desc="Price Column">-->
        <v-col
          v-if="yachtModel"
          lg="4"
          class="px-6 fill-height pt-4"
          style="overflow-y: auto"
        >
          <v-row no-gutters>
            <v-col class="pr-2">
              <v-select
                v-model="currency"
                :items="['EUR', 'GBP', 'USD', 'SEK']"
                label="Sale Currency"
                hide-details
                outlined
                dense
              />
            </v-col>
            <v-col class="pl-2">
              <v-select
                v-model="productionYear"
                :items="deliveryYearSelectItems"
                name="pricelist_year"
                label="Pricelist / Sale Year"
                outlined
                dense
                hide-details
              />
            </v-col>
          </v-row>
          <div class="pa-4 subtitle-1">
            Yacht Annex Price
          </div>
          <small-pricing-list
            class="mb-6"
            live-calculation
            :equipment-packs="selectedEquipmentPacks"
            :base-price="yachtModel.sale_price"
            :base-currency="yachtModel.currency"
            :equipment-price="countryData.equipment_sale_price"
            :cdr-cost="countryData.cost_cdr"
            :currency="currency"
            :total-price="countryData.total_sale_price"
            :currency-exchange-year="productionYear"
            :model-year="yachtModel.model_year"
            :production-year="productionYear"
            :yearly-price-increase="Number(yachtModel.manufacturer.yearly_price_increase)"
          />
        </v-col>
        <!--</editor-fold>-->
      </v-row>
    </v-card-text>

    <yacht-equipment-pack-edit-dialog
      :yacht-equipment-pack="yachtEquipmentPackEdit.equipmentPack"
      :visible.sync="yachtEquipmentPackEdit.visible"
      :editing-custom="yachtEquipmentPackEdit.equipmentPack && yachtEquipmentPackEdit.equipmentPack.custom"
      custom
      @yachtEquipmentPack:created="updateCustomEquipmentPacks"
      @yachtEquipmentPack:updated="updateCustomEquipmentPacks"
      @yachtEquipmentPack:deleted="removeCustomEquipmentPack"
    />
  </content-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import YachtEquipmentPack from '@/models/YachtEquipmentPack'
import ContentDialog from '@/components/helpers/Dialog'
import YachtModel from '@/models/YachtModel'
import SmallPricingList from '@/components/business/yachts/SmallPricingList'
import { cloneDeep, sortBy } from 'lodash'
import YachtEquipmentPackEditDialog from '@/components/settings/yachtEquipmentPacks/YachtEquipmentPackEditDialog'

export default {
  name: 'YachtModelConfigureEquipmentDialog',
  components: {
    YachtEquipmentPackEditDialog,
    SmallPricingList,
    ContentDialog
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    yachtModelId: {
      type: Number,
      required: true
    },
    yacht: {
      type: Object,
      default: () => undefined
    },
    countryId: {
      type: Number,
      default: undefined
    },
    selectedPacks: {
      type: Array,
      default: () => []
    },
    selectedCurrency: {
      type: String,
      default: undefined
    },
    selectedProductionYear: {
      type: Number,
      default: undefined
    },
    updatesPullable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loading: false,
      yachtModel: undefined,
      equipmentPacksDirty: [],
      currentCountryId: null,
      currentCategoryId: null,
      currentEquipmentType: 'all',
      currency: 'EUR',
      productionYear: null,
      yachtEquipmentPackEdit: {
        equipmentPack: undefined,
        visible: false
      }
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    filteredEquipmentPacks() {
      return this.equipmentPacksDirty.filter(ep => {
        if (this.currentCategoryId) return ep.yacht_equipment_pack_category_id === this.currentCategoryId

        return true
      }).filter(ep => {
        if (this.currentEquipmentType === 'required') return !!ep.default
        if (this.currentEquipmentType === 'optional') return !ep.default
        if (this.currentEquipmentType === 'custom') return !!ep.custom

        return true
      })
    },
    selectedEquipmentPacks() {
      return this.equipmentPacksDirty.filter(ep => !!ep.selected)
    },
    countryData() {
      if (!this.currentCountryId) return {}

      const country = this.yachtModel.countries.find(c => c.id === parseInt(this.currentCountryId))

      return country ? country.pivot : {}
    },
    currentCountry() {
      if (!this.currentCountryId) return {}

      const country = this.countries.data.find(c => c.id === this.currentCountryId)

      return country || {}
    },
    categories() {
      return sortBy(this.equipmentPackCategories.data.map(category => {
          return {
            ...category,
            isFiltered: this.currentCategoryId === category.id
          }
        }), 'order')
    },
    deliveryYearSelectItems() {
      let items = []
      for (let i = 0; i < 20; i++) { items.push(this.currentYear + i) }
      return items
    },
    ...mapGetters('settings', ['equipmentPackCategories', 'countries'])
  },
  watch: {
    modalVisible: function (value) {
      if (!value) return

      if (this.countryId) this.currentCountryId = this.countryId

      this.fetchYachtModel()
    }
  },
  created() {
    this.currentYear = new Date().getFullYear()
    this.installedByData = YachtEquipmentPack.installedByData

    if (this.countryId) this.currentCountryId = this.countryId

    this.fetchYachtModel()
  },
  methods: {
    async fetchYachtModel() {
      try {
        this.loading = false

        this.yachtModel = await YachtModel.api.find(this.yachtModelId)

        let equipmentPacks = cloneDeep(this.yachtModel.equipment_packs.filter(ep => {
          return ep.countries.some(c => c.id === parseInt(this.currentCountryId)) && ep.status === YachtEquipmentPack.STATUS_ACTIVE
        })).map(ep => {
          let selectedPack = this.selectedPacks.find(sep => sep.yacht_equipment_pack_id === ep.id)

          let returnPack

          if (selectedPack) {
            returnPack = {
              // ...ep,
              ...selectedPack,
              is_original_different: selectedPack.is_original_different,
              id: selectedPack.yacht_equipment_pack_id, // needed cause backend requires original equipment_pack_id when syncing
              selected: true
            }

            if (selectedPack.is_configurable) {
              returnPack.items = selectedPack.items.map(item => {
                return {
                  ...item,
                  is_selected: !!item.is_selected
                }
              })
            }
          } else {
            returnPack = {
              ...ep,
              selected: this.selectedPacks.length > 0 ? false : !!ep.default
            }

            if (ep.is_configurable) {
              returnPack.items = ep.items.map(item => {
                return {
                  ...item,
                  is_selected: returnPack.selected
                }
              })
            }
          }

          return {
            ...returnPack,
            is_expanded: ep.installed_by === YachtEquipmentPack.INSTALLED_BY_SHIPYARD
          }
        })

        // add selected packs if missing from the model
        for (let selectedPack of this.selectedPacks) {
          if (equipmentPacks.some(ep => ep.id === selectedPack.yacht_equipment_pack_id)) continue

          equipmentPacks.push({
              ...selectedPack,
              id: selectedPack.yacht_equipment_pack_id, // needed cause backend requires original equipment_pack_id when syncing
              selected: true,
              is_original_removed: true
            }
          )
        }

        this.equipmentPacksDirty = sortBy(equipmentPacks, 'category.order')

        this.currency = this.selectedCurrency || this.yachtModel.currency
        this.productionYear = this.selectedProductionYear || Number(this.yachtModel.model_year)
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'There was a problem getting the model',
          color: 'error'
        })
        throw e
      } finally {
        this.loading = false
      }
    },
    toggleEquipmentPack(id) {
      let index = this.equipmentPacksDirty.findIndex(ep => ep.id === id)

      if (index === -1) return

      let equipmentPack = this.equipmentPacksDirty[index]

      if (equipmentPack.selected && equipmentPack.default && !this.$can('edit-default-equipment-packs', 'Yacht')) return

      equipmentPack.selected = !equipmentPack.selected

      this.equipmentPacksDirty.splice(index, equipmentPack)
    },
    saveConfiguration() {
      this.$emit('configuration', {
        yachtModelId: this.yachtModel.id,
        yachtModel: this.yachtModel,
        countryId: Number(this.currentCountryId),
        equipmentPacks: this.equipmentPacksDirty.filter(ep => !!ep.selected),
        currency: this.currency,
        productionYear: this.productionYear
      })

      this.modalVisible = false
    },
    updateCustomEquipmentPacks(yachtEquipmentPack) {
      yachtEquipmentPack = {
        ...yachtEquipmentPack,
        is_expanded: false,
        selected: true
      }

      const index = this.equipmentPacksDirty.findIndex(ep => ep.id === yachtEquipmentPack.id)

      if (index > -1) {
        this.equipmentPacksDirty.splice(index, 1, yachtEquipmentPack)
        return
      }

      this.equipmentPacksDirty.push(yachtEquipmentPack)
    },
    removeCustomEquipmentPack(yachtEquipmentPackId) {
      const index = this.equipmentPacksDirty.findIndex(ep => ep.id === yachtEquipmentPackId)

      if (index === -1) return

      this.equipmentPacksDirty.splice(index, 1)
    },
    calculateConfigurablePackPrice(pack) {
      if (!pack.is_configurable) return

      let salePrice = pack.items.map(item => item.is_selected ? Number(item.sale_price) : 0).reduce((a, b) => a + b)
      let purchasePrice = pack.items.map(item => item.is_selected ? Number(item.purchase_price) : 0).reduce((a, b) => a + b)

      pack.sale_price = salePrice
      pack.purchase_price = purchasePrice
    },
    pullUpdatedPack(pack) {
      let updatedPack = cloneDeep(this.yachtModel.equipment_packs.find(ep => ep.id === pack.id))

      if (!updatedPack) return

      updatedPack.selected = pack.selected
      updatedPack.is_expanded = !pack.is_expanded

      if (updatedPack.is_configurable) {
        updatedPack.items = updatedPack.items.map(item => {
          return {
            ...item,
            is_selected: updatedPack.selected
          }
        })
      }

      const index = this.equipmentPacksDirty.findIndex(ep => ep.id === pack.id)

      this.equipmentPacksDirty.splice(index, 1, updatedPack)
    },
    removeUpdatedPack(pack) {
      const index = this.equipmentPacksDirty.findIndex(ep => ep.id === pack.id)

      if (index === -1) return

      this.equipmentPacksDirty.splice(index, 1)
    }

  }
}
</script>
<style lang="stylus">
.clickable
  &:hover
    background: #eeeeee;
</style>
